<div class="text-fg w-full max-w-none p-6">
  <!-- Loading -->
  @if (loading()) {
    <div class="flex items-center justify-center gap-2 p-12 text-sm text-muted">
      <ui-spinner size="sm" />
      <span>Loading...</span>
    </div>
  } @else if (error() && !app()) {
    <div class="p-8 text-center text-danger">
      <p>{{ error() }}</p>
      <button type="button" uiButton variant="outline" size="sm" class="mt-3" (click)="loadApp()">Retry</button>
    </div>
  } @else if (app(); as a) {
    <!-- Toasts -->
    @if (success()) {
      <div class="fixed right-4 top-4 z-[9999] flex items-center gap-2 rounded-lg border px-4 py-2.5 text-[0.82rem] font-medium shadow-lg border-success-border bg-success-bg text-success-fg">
        <ng-icon name="lucideCheck" size="14" />
        {{ success() }}
      </div>
    }
    @if (error()) {
      <div class="fixed right-4 top-4 z-[9999] flex items-center gap-2 rounded-lg border px-4 py-2.5 text-[0.82rem] font-medium shadow-lg border-danger-border bg-danger-bg text-danger-fg">
        <ng-icon name="lucideTriangleAlert" size="14" />
        {{ error() }}
      </div>
    }

    <!-- Header -->
    <header class="mb-6">
      <button type="button" class="mb-3 inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[0.85rem] text-muted hover:text-primary" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="m-0 text-[1.35rem] font-bold text-fg">{{ a.name }}</h1>
          <span class="mb-2 mt-0.5 block text-[0.85rem] text-muted">{{ a.merchant_name }}</span>
          <div class="flex items-center gap-2">
            <ui-badge [variant]="getEnvVariant(a.environment)" class="uppercase">{{ a.environment }}</ui-badge>
            <ui-badge [variant]="a.is_active ? 'success' : 'danger'">{{ a.is_active ? 'Active' : 'Inactive' }}</ui-badge>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button type="button" uiButton variant="outline" size="sm" (click)="loadApp(); loadApiKeys(); loadStats()" title="Refresh">
            <ng-icon name="lucideRefreshCw" size="16" />
          </button>
          <button
            type="button"
            uiButton
            size="sm"
            [variant]="a.is_active ? 'danger' : 'primary'"
            (click)="confirmToggleActive()"
          >
            {{ a.is_active ? 'Deactivate' : 'Activate' }}
          </button>
        </div>
      </div>
    </header>

    <!-- Merchant Info -->
    @if (merchant(); as m) {
      <section class="mb-5 rounded-xl border border-border bg-card p-5">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="m-0 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
            <ng-icon name="lucideStore" size="16" />
            Merchant
          </h3>
          <a [routerLink]="shellRoutes.commands('manage', 'merchants', m.id)" uiButton variant="outline" size="sm">
            <ng-icon name="lucideExternalLink" size="12" />
            View Merchant Hub
          </a>
        </div>
        <div class="rounded-lg border border-border bg-surface-muted p-3.5">
          <div class="mb-2.5">
            <div class="text-base font-semibold text-fg">{{ m.name }}</div>
            @if (m.legal_name) {
              <div class="text-[0.78rem] text-muted">{{ m.legal_name }}</div>
            }
          </div>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
            <div class="flex flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Email</span>
              <span class="text-[0.85rem] font-medium text-fg">{{ m.email }}</span>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Country</span>
              <span class="text-[0.85rem] font-medium text-fg">{{ m.country_name }} ({{ m.country_code }})</span>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Applications</span>
              <span class="text-[0.85rem] font-medium text-fg">{{ m.application_count }}</span>
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Status</span>
              <span class="text-[0.85rem] font-medium text-fg">
                <ui-badge [variant]="m.is_active ? 'success' : 'danger'">{{ m.is_active ? 'Active' : 'Inactive' }}</ui-badge>
              </span>
            </div>
          </div>
        </div>
      </section>
    }

    <!-- Application Info -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <h3 class="m-0 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
        <ng-icon name="lucideServer" size="16" />
        Application Info
      </h3>
      <div class="mb-2 grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Client ID</span>
          <span class="inline-flex cursor-pointer items-center gap-1 text-[0.85rem] font-medium text-fg hover:text-primary" (click)="copyToClipboard(a.client_id)" title="Click to copy">
            <code class="rounded bg-surface-muted px-1.5 py-0.5 text-[0.78rem]">{{ a.client_id }}</code>
            <ng-icon name="lucideClipboard" size="12" />
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Environment</span>
          <span class="text-[0.85rem] font-medium text-fg">
            <ui-badge [variant]="getEnvVariant(a.environment)" class="uppercase">{{ a.environment }}</ui-badge>
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Active Keys</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ a.active_key_count }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Last Used</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ formatDate(a.last_used_at) }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Created</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ formatDate(a.created_at) }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Updated</span>
          <span class="text-[0.85rem] font-medium text-fg">{{ formatDate(a.updated_at) }}</span>
        </div>
      </div>
      @if (a.description) {
        <div class="mt-2">
          <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Description</span>
          <p class="mx-0 mb-0 mt-1 text-[0.85rem] text-fg-secondary">{{ a.description }}</p>
        </div>
      }
    </section>

    <!-- Settings Section -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="m-0 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideShieldCheck" size="16" />
          Settings
        </h3>
        @if (!settingsEditing()) {
          <button type="button" uiButton variant="outline" size="sm" (click)="startEditSettings()">
            <ng-icon name="lucidePencil" size="12" />
            Edit
          </button>
        }
      </div>

      @if (settingsEditing()) {
        <!-- Edit Mode -->
        <div>
          <div class="mb-4 last:mb-0">
            <label class="mb-1.5 flex items-center gap-1.5 text-[0.82rem] font-semibold text-fg-secondary">Description</label>
            <textarea uiInput class="resize-y" [(ngModel)]="editDescription" rows="2" placeholder="Application description..."></textarea>
          </div>
          <div class="mb-4 last:mb-0">
            <label class="mb-1.5 flex items-center gap-1.5 text-[0.82rem] font-semibold text-fg-secondary">
              <ng-icon name="lucideGlobe" size="14" />
              Allowed IPs
            </label>
            <p class="mx-0 mb-2 mt-0 text-xs text-muted">Restrict API access to specific IP addresses or CIDR blocks. Leave empty to allow all.</p>
            <div class="mb-2 flex flex-wrap gap-1.5">
              @for (ip of editAllowedIps; track ip) {
                <span class="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-info-bg text-info-fg">
                  {{ ip }}
                  <button type="button" class="flex cursor-pointer border-none bg-transparent p-0 leading-none text-inherit opacity-70 hover:opacity-100" (click)="removeIp(ip)">
                    <ng-icon name="lucideX" size="10" />
                  </button>
                </span>
              }
            </div>
            <div class="flex items-center gap-2">
              <input
                type="text"
                uiInput size="sm" class="flex-1 font-mono"
                [(ngModel)]="newIpValue"
                placeholder="e.g. 192.168.1.1 or 10.0.0.0/8"
                (keyup.enter)="addIp()"
              />
              <button type="button" uiButton variant="outline" size="sm" (click)="addIp()" [disabled]="!newIpValue.trim()">
                <ng-icon name="lucidePlus" size="12" />
                Add
              </button>
            </div>
          </div>
          <div class="mb-4 last:mb-0">
            <label class="mb-1.5 flex items-center gap-1.5 text-[0.82rem] font-semibold text-fg-secondary">
              <ng-icon name="lucideShieldCheck" size="14" />
              Allowed Scopes
            </label>
            <p class="mx-0 mb-2 mt-0 text-xs text-muted">
              Select operation scopes for API access. Empty list means no API access. Choose &quot;All scopes&quot; for full access.
            </p>
            <div class="flex flex-wrap gap-1.5">
              @for (choice of scopeChoices(); track choice.value) {
                <label
                  class="inline-flex cursor-pointer select-none items-center gap-1 rounded-full border px-2.5 py-1 text-[0.72rem] font-medium transition-colors"
                  [class]="isScopeSelected(choice.value) ? 'border-success-border bg-success-bg text-success-fg' : 'border-border bg-card text-fg hover:border-border-strong hover:bg-surface-muted'"
                >
                  <input class="hidden" type="checkbox" [checked]="isScopeSelected(choice.value)" (change)="toggleScope(choice.value)" />
                  {{ choice.label }}
                </label>
              }
            </div>
          </div>
          <div class="mt-4 flex justify-end gap-2 border-t border-border pt-3">
            <button type="button" uiButton variant="outline" (click)="cancelEditSettings()" [disabled]="settingsSaving()">Cancel</button>
            <button type="button" uiButton variant="primary" (click)="saveSettings()" [disabled]="settingsSaving()">
              @if (settingsSaving()) { Saving... } @else {
                <ng-icon name="lucideCheck" size="14" />
                Save Settings
              }
            </button>
          </div>
        </div>
      } @else {
        <!-- View Mode -->
        <div class="flex flex-col gap-3">
          <div class="mb-4 last:mb-0">
            <label class="mb-1.5 flex items-center gap-1.5 text-[0.82rem] font-semibold text-fg-secondary">
              <ng-icon name="lucideGlobe" size="14" />
              Allowed IPs
            </label>
            @if (a.allowed_ips.length === 0) {
              <span class="text-[0.82rem] italic text-muted">All IPs allowed (no restriction)</span>
            } @else {
              <div class="mb-2 flex flex-wrap gap-1.5">
                @for (ip of a.allowed_ips; track ip) {
                  <span class="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium cursor-default bg-info-bg text-info-fg">{{ ip }}</span>
                }
              </div>
            }
          </div>
          <div class="mb-4 last:mb-0">
            <label class="mb-1.5 flex items-center gap-1.5 text-[0.82rem] font-semibold text-fg-secondary">
              <ng-icon name="lucideShieldCheck" size="14" />
              Allowed Scopes
            </label>
            @if (a.allowed_scopes.length === 0) {
              <span class="text-[0.82rem] italic text-muted">No API access (no scopes selected)</span>
            } @else {
              <div class="mb-2 flex flex-wrap gap-1.5">
                @for (scope of a.allowed_scopes; track scope) {
                  <span class="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium cursor-default bg-success-bg text-success-fg">{{ scopeLabel(scope) }}</span>
                }
              </div>
            }
          </div>
        </div>
      }
    </section>

    <!-- API Keys Section -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="m-0 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideKey" size="16" />
          API Keys
        </h3>
        <button type="button" uiButton variant="primary" size="sm" (click)="openCreateKeyModal()">
          <ng-icon name="lucidePlus" size="12" />
          Create Key
        </button>
      </div>

      @if (apiKeys().length === 0) {
        <ui-empty-state title="No API keys yet. Create one to start authenticating API requests." />
      } @else {
        <div uiTableWrap>
          <table uiTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (key of apiKeys(); track key.id) {
                <tr [class.opacity-60]="!key.is_active" [class.text-muted]="key.is_expired">
                  <td class="font-medium">{{ key.name }}</td>
                  <td><code class="rounded bg-surface-muted px-1.5 py-0.5 text-[0.78rem]">{{ key.secret_prefix }}...</code></td>
                  <td>
                    @if (key.is_expired) {
                      <ui-badge variant="danger">Expired</ui-badge>
                    } @else if (key.is_active) {
                      <ui-badge variant="success">Active</ui-badge>
                    } @else {
                      <ui-badge variant="neutral">Inactive</ui-badge>
                    }
                  </td>
                  <td>{{ key.expires_at ? formatDate(key.expires_at) : 'Never' }}</td>
                  <td>{{ formatDate(key.last_used_at) }}</td>
                  <td>{{ formatDate(key.created_at) }}</td>
                  <td><div class="flex gap-1.5">
                    <button
                      type="button"
                      uiButton
                      variant="ghost"
                      size="sm"
                      [ngClass]="key.is_active ? 'hover:!text-warning' : 'hover:!text-success'"
                      (click)="confirmToggleKeyActive(key)"
                      [title]="key.is_active ? 'Deactivate' : 'Activate'"
                    >
                      @if (key.is_active) {
                        <ng-icon name="lucideEyeOff" size="14" />
                      } @else {
                        <ng-icon name="lucideEye" size="14" />
                      }
                    </button>
                    <button type="button" uiButton variant="ghost" size="sm" class="hover:!text-primary" (click)="confirmRotateKey(key)" title="Rotate">
                      <ng-icon name="lucideRefreshCw" size="14" />
                    </button>
                    <button type="button" uiButton variant="ghost" size="sm" class="hover:!text-danger" (click)="confirmDeleteKey(key)" title="Delete">
                      <ng-icon name="lucideTrash2" size="14" />
                    </button>
                  </div></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>

    <!-- Activity Stats Section -->
    <section class="mb-5 rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="m-0 flex items-center gap-1.5 text-[0.95rem] font-semibold text-fg">
          <ng-icon name="lucideChartColumn" size="16" />
          Activity
        </h3>
        <select uiInput size="sm" class="!w-auto cursor-pointer" [(ngModel)]="statsPeriod" (ngModelChange)="onPeriodChange()">
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      @if (stats(); as s) {
        <div class="mb-4 grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
          <div class="flex flex-col gap-0.5 rounded-lg border border-border bg-surface-muted p-3.5">
            <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Total Transactions</span>
            <span class="text-[1.15rem] font-bold text-fg">{{ getTotalTransactions() }}</span>
          </div>
          <div class="flex flex-col gap-0.5 rounded-lg border border-border bg-surface-muted p-3.5">
            <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">Success Volume</span>
            <span class="text-[1.15rem] font-bold text-fg">{{ formatAmount(getTotalSuccessAmount()) }}</span>
          </div>
          <div class="flex flex-col gap-0.5 rounded-lg border border-border bg-surface-muted p-3.5">
            <span class="text-[0.68rem] font-medium uppercase tracking-wider text-muted">API Keys</span>
            <span class="text-[1.15rem] font-bold text-fg">{{ s.api_keys.active }} / {{ s.api_keys.total }}</span>
          </div>
        </div>

        <div>
          <h4 class="m-0 mb-2.5 text-[0.85rem] font-semibold text-fg-secondary">Transaction Breakdown</h4>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
            @for (type of ['deposits', 'payouts', 'refunds', 'remittances']; track type) {
              <div class="rounded-lg border border-border p-3">
                <span class="mb-1.5 block text-[0.78rem] font-semibold capitalize text-fg-secondary">{{ type | titlecase }}</span>
                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg">
                    <span>Total</span>
                    <strong>{{ $any(s.transactions)[type].total }}</strong>
                  </div>
                  <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-success">
                    <span>Success</span>
                    <strong>{{ $any(s.transactions)[type].success }}</strong>
                  </div>
                  <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-danger">
                    <span>Failed</span>
                    <strong>{{ $any(s.transactions)[type].failed }}</strong>
                  </div>
                  <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-warning">
                    <span>Pending</span>
                    <strong>{{ $any(s.transactions)[type].pending }}</strong>
                  </div>
                  <div class="flex justify-between text-[0.78rem] text-muted [&>strong]:text-fg mt-0.5 border-t border-border pt-1">
                    <span>Volume</span>
                    <strong>{{ formatAmount($any(s.transactions)[type].success_amount) }}</strong>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>

        @if (s.recent_audit.length > 0) {
          <div class="mt-4">
            <h4 class="m-0 mb-2.5 text-[0.85rem] font-semibold text-fg-secondary">Recent Audit Events</h4>
            <div class="flex flex-col gap-1">
              @for (entry of s.recent_audit; track entry.id) {
                <div class="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-[0.78rem]">
                  <span class="font-medium text-fg-secondary">{{ entry.action }}</span>
                  <span class="text-[0.72rem] text-muted">{{ formatDate(entry.timestamp) }}</span>
                </div>
              }
            </div>
          </div>
        }
      } @else {
        <ui-empty-state title="No activity data available." />
      }
    </section>
  }

  <!-- Copy toast -->
  @if (copyToast()) {
    <div class="fixed bottom-8 left-1/2 z-[9999] -translate-x-1/2 rounded-lg bg-fg px-4 py-2 text-[0.8rem] font-medium text-card shadow-lg">Copied!</div>
  }

  <!-- ═══════════ CREATE KEY MODAL ═══════════ -->
  <ui-modal title="Create API Key" [open]="createKeyOpen()" (closed)="closeCreateKeyModal()">
    @if (createKeyError()) {
      <ui-alert variant="danger">{{ createKeyError() }}</ui-alert>
    }
    <div class="mb-3.5">
      <label for="keyName" class="mb-1 block text-[0.78rem] font-medium text-fg-secondary">Key Name</label>
      <input
        id="keyName"
        type="text"
        uiInput
        [(ngModel)]="newKeyName"
        placeholder="e.g. Primary Key, Backup Key"
        (keyup.enter)="submitCreateKey()"
      />
    </div>
    <div>
      <label for="keyExpiry" class="mb-1 block text-[0.78rem] font-medium text-fg-secondary">Expiry Date (optional)</label>
      <input id="keyExpiry" type="date" uiInput [(ngModel)]="newKeyExpiry" />
    </div>
    <ng-container modal-footer>
      <button type="button" uiButton variant="outline" (click)="closeCreateKeyModal()" [disabled]="createKeySaving()">Cancel</button>
      <button type="button" uiButton variant="primary" (click)="submitCreateKey()" [disabled]="createKeySaving()">
        @if (createKeySaving()) { Creating... } @else { Create Key }
      </button>
    </ng-container>
  </ui-modal>

  <!-- ═══════════ SECRET REVEAL MODAL (locked: no dismiss) ═══════════ -->
  <ui-modal title="API Key Secret — One-Time Display" size="lg" [open]="secretRevealOpen()">
    <div class="mb-4 rounded-md border border-warning-border bg-warning-bg px-3.5 py-3 text-[0.82rem] leading-snug text-warning-fg">
      <strong class="mb-1.5 block text-[0.88rem]">This is the only time you will see this secret.</strong>
      <p class="mx-0 mb-0 mt-1.5 text-[0.78rem]">For security reasons, the secret is not stored in our system and <em>cannot be recovered</em> after you close this dialog. Copy it now and store it in a safe place (e.g. a password manager or your server's environment variables).</p>
      <p class="mx-0 mb-0 mt-1.5 text-[0.78rem]">If you lose this secret, you will need to rotate or create a new API key.</p>
    </div>
    <div class="flex flex-col gap-3">
      <div>
        <span class="mb-1 block text-[0.68rem] font-medium uppercase tracking-wider text-muted">Key Name</span>
        <span class="text-[0.85rem] font-medium text-fg">{{ revealedKeyName }}</span>
      </div>
      <div>
        <span class="mb-1 block text-[0.68rem] font-medium uppercase tracking-wider text-muted">Secret</span>
        <div class="flex items-start gap-2">
          <code class="flex-1 break-all rounded-md border border-border bg-surface-muted p-2 font-mono text-[0.78rem]">{{ revealedSecret }}</code>
          <button type="button" uiButton size="sm" [variant]="secretCopied ? 'primary' : 'outline'" (click)="copySecret()">
            @if (secretCopied) {
              <ng-icon name="lucideCheck" size="12" />
              Copied
            } @else {
              <ng-icon name="lucideClipboard" size="12" />
              Copy
            }
          </button>
        </div>
      </div>
    </div>
    <ng-container modal-footer>
      <button type="button" uiButton variant="outline" class="me-auto" [class.!text-success]="secretShared" (click)="shareSecret()">
        @if (secretShared) {
          <ng-icon name="lucideCheck" size="12" />
          Shared to clipboard
        } @else {
          <ng-icon name="lucideShare2" size="12" />
          Share (copy all details)
        }
      </button>
      <button type="button" uiButton variant="primary" (click)="closeSecretReveal()" [disabled]="!secretCopied">
        @if (secretCopied) {
          I've saved the secret
        } @else {
          Copy the secret first
        }
      </button>
    </ng-container>
  </ui-modal>

  <!-- ═══════════ CONFIRMATION MODAL ═══════════ -->
  <ui-modal [title]="confirmTitle" size="sm" [open]="confirmOpen()" (closed)="closeConfirm()">
    <p class="m-0">{{ confirmMessage }}</p>
    <ng-container modal-footer>
      <button type="button" uiButton variant="outline" (click)="closeConfirm()" [disabled]="confirmSaving()">Cancel</button>
      <button type="button" uiButton [variant]="confirmBtnVariant" (click)="executeConfirm()" [disabled]="confirmSaving()">
        @if (confirmSaving()) { Working... } @else { {{ confirmBtnLabel }} }
      </button>
    </ng-container>
  </ui-modal>
</div>
